/**
 * Khách đi lại trên lưới (lớp render, M2).
 *
 * - Tỷ lệ xuất hiện suy từ core khach (kyVongLuotKhach — thống kê thật),
 *   nhân thêm hệ số mật độ trình diễn để cửa hàng trông sống động.
 * - Đường đi dùng hàm thuần timDuong/oTuongTac của src/core (BFS).
 * - Khách hình ảnh CHỈ minh họa: việc mua bán thật vẫn do core tick
 *   xử lý theo thống kê (không tính trùng).
 * - Xếp hàng tại quầy thu ngân: hàng đợi FIFO trên các ô trống
 *   phía trước ô tương tác của quầy.
 *
 * Hằng số trình diễn (không ảnh hưởng kinh tế — xem decisions-m2.md):
 * tốc độ đi, hệ số mật độ, thời gian thanh toán, số khách tối đa.
 */
import * as THREE from 'three';
import { idAssetKhach, veCanvasPlaceholder } from '../assets/index.js';
import { kieuKhachMoTheoGio, kyVongLuotKhach, gioTrongNgay } from '../core/khach.js';
import {
  MA_QUAY_THU_NGAN,
  kichThuocKhuVuc,
  oCuaRaVao,
  oTuongTac,
  tapOChiem,
  timDuong,
} from '../core/noi-that.js';
import { taoRng, type HamRng } from '../core/rng.js';
import type { DuLieuGame, MonNoiThatDat, OLuoi, TrangThaiGame } from '../core/types.js';
import { oSangTheGioi } from './scene.js';

/** Tốc độ đi bộ: ô lưới mỗi giây thực. */
const TOC_DO_DI = 2.5;
/** Hệ số mật độ trình diễn: nhân vào kỳ vọng thống kê để nhìn thấy khách. */
const HE_SO_MAT_DO = 12;
/** Thời gian thanh toán mỗi khách (giây thực). */
const THOI_GIAN_THANH_TOAN = 2;
/** Trần khách cùng lúc (hiệu năng). */
const TOI_DA_KHACH = 24;

type GiaiDoan = 'den-ke' | 'cho-thanh-toan' | 'thanh-toan' | 'ra-cua';

interface KhachAo {
  id: number;
  sprite: THREE.Sprite;
  tenKieu: string;
  giaiDoan: GiaiDoan;
  duong: OLuoi[];
  chiSo: number;
  henGio: number;
}

export class QuanLyKhach {
  private readonly scene: THREE.Scene;
  private readonly rng: HamRng;
  private readonly ketCau: Map<string, THREE.CanvasTexture> = new Map();
  private dsKhach: KhachAo[] = [];
  private diemSpawn = 0;
  private idTiep = 1;
  /** UID khách đang thanh toán (một lúc một người mỗi quầy — M2 một quầy). */
  private dangThanhToan = false;

  constructor(scene: THREE.Scene, seed = 12345) {
    this.scene = scene;
    this.rng = taoRng(seed);
  }

  private layKetCau(tenKieu: string): THREE.CanvasTexture {
    let tex = this.ketCau.get(tenKieu);
    if (!tex) {
      tex = new THREE.CanvasTexture(veCanvasPlaceholder(idAssetKhach(tenKieu)));
      tex.colorSpace = THREE.SRGBColorSpace;
      this.ketCau.set(tenKieu, tex);
    }
    return tex;
  }

  /** Xóa hết khách hình ảnh (gọi khi bố cục nội thất đổi — đường đi cũ hết hiệu lực). */
  xoaTatCa(): void {
    for (const k of this.dsKhach) {
      this.scene.remove(k.sprite);
      k.sprite.material.dispose();
    }
    this.dsKhach = [];
    this.dangThanhToan = false;
    this.diemSpawn = 0;
  }

  soKhach(): number {
    return this.dsKhach.length;
  }

  private theGioi(o: OLuoi, rong: number, cao: number): THREE.Vector3 {
    const p = oSangTheGioi(o.x, o.y, rong, cao);
    return new THREE.Vector3(p.x, 0.55, p.z);
  }

  private chonKieuKhach(state: TrangThaiGame, duLieu: DuLieuGame): string | null {
    const gio = gioTrongNgay(state.tongGioGame);
    const mo = kieuKhachMoTheoGio(gio, duLieu);
    if (mo.length === 0) return null;
    const trongSo = mo.map((k) => kyVongLuotKhach(k, state, duLieu, 1));
    const tong = trongSo.reduce((a, b) => a + b, 0);
    if (tong <= 0) return null;
    let r = this.rng() * tong;
    for (let i = 0; i < mo.length; i++) {
      r -= trongSo[i]!;
      if (r <= 0) return mo[i]!.ten;
    }
    return mo[mo.length - 1]!.ten;
  }

  private sinhKhach(state: TrangThaiGame, duLieu: DuLieuGame): void {
    if (this.dsKhach.length >= TOI_DA_KHACH) return;
    const tenKieu = this.chonKieuKhach(state, duLieu);
    if (!tenKieu) return;
    const kv = kichThuocKhuVuc(state.capCuaHang, duLieu);
    const cua = oCuaRaVao(kv);
    // Chọn kệ/tủ còn ô tương tác.
    const keList = state.boCuc.filter((m) => m.ma === 'KE' || m.ma === 'TU_LANH');
    const keDat = keList
      .map((ke) => ({ ke, oTT: oTuongTac(ke, state.boCuc, kv, duLieu) }))
      .filter((x) => x.oTT !== null);
    if (keDat.length === 0) return;
    const { oTT } = keDat[Math.floor(this.rng() * keDat.length)]!;
    const duong = timDuong(cua, oTT!, state.boCuc, kv, duLieu);
    if (!duong) return;

    const sprite = new THREE.Sprite(
      new THREE.SpriteMaterial({ map: this.layKetCau(tenKieu), transparent: true }),
    );
    sprite.scale.set(0.9, 0.9, 1);
    sprite.position.copy(this.theGioi(duong[0]!, kv.rong, kv.cao));
    this.scene.add(sprite);
    this.dsKhach.push({
      id: this.idTiep++,
      sprite,
      tenKieu,
      giaiDoan: 'den-ke',
      duong,
      chiSo: 0,
      henGio: 0,
    });
  }

  private quayThuNgan(state: TrangThaiGame): MonNoiThatDat | null {
    return state.boCuc.find((m) => m.ma === MA_QUAY_THU_NGAN) ?? null;
  }

  /** Ô xếp hàng: các ô trống liên tiếp phía nam ô tương tác của quầy. */
  private oHangDoi(quay: MonNoiThatDat, state: TrangThaiGame, duLieu: DuLieuGame): OLuoi[] {
    const kv = kichThuocKhuVuc(state.capCuaHang, duLieu);
    const oTT = oTuongTac(quay, state.boCuc, kv, duLieu);
    if (!oTT) return [];
    const chiem = tapOChiem(state.boCuc, duLieu);
    const cua = oCuaRaVao(kv);
    const slots: OLuoi[] = [];
    for (let i = 1; i <= 6; i++) {
      const o = { x: oTT.x, y: oTT.y - i };
      if (o.y < 0 || o.y >= kv.cao) break;
      if (chiem.has(`${o.x},${o.y}`)) break;
      if (o.x === cua.x && o.y === cua.y) break; // không chặn ô cửa
      slots.push(o);
    }
    return slots;
  }

  private diChuyen(k: KhachAo, dt: number, rong: number, cao: number): boolean {
    // Trả về true khi đã đi hết đường.
    let quangDuong = TOC_DO_DI * dt;
    while (quangDuong > 0 && k.chiSo < k.duong.length - 1) {
      const hienTai = this.theGioi(k.duong[k.chiSo]!, rong, cao);
      const tiep = this.theGioi(k.duong[k.chiSo + 1]!, rong, cao);
      const doan = hienTai.distanceTo(tiep);
      const conLai = doan - k.sprite.position.distanceTo(hienTai);
      if (quangDuong >= conLai) {
        quangDuong -= conLai;
        k.chiSo += 1;
        k.sprite.position.copy(tiep);
      } else {
        k.sprite.position.lerp(tiep, quangDuong / doan);
        quangDuong = 0;
      }
    }
    return k.chiSo >= k.duong.length - 1;
  }

  private loaiBo(k: KhachAo): void {
    this.scene.remove(k.sprite);
    k.sprite.material.dispose();
    this.dsKhach = this.dsKhach.filter((x) => x.id !== k.id);
    if (k.giaiDoan === 'thanh-toan') this.dangThanhToan = false;
  }

  /** Cập nhật mỗi khung hình. dtGiay: giây thực; dtGioGame: giờ game đã trôi. */
  capNhat(dtGiay: number, dtGioGame: number, state: TrangThaiGame, duLieu: DuLieuGame): void {
    const kv = kichThuocKhuVuc(state.capCuaHang, duLieu);

    // Spawn theo kỳ vọng thống kê × hệ số trình diễn.
    const gio = gioTrongNgay(state.tongGioGame);
    let tongKyVong = 0;
    for (const kieu of kieuKhachMoTheoGio(gio, duLieu)) {
      tongKyVong += kyVongLuotKhach(kieu, state, duLieu, 1);
    }
    this.diemSpawn += dtGioGame * HE_SO_MAT_DO * tongKyVong;
    while (this.diemSpawn >= 1) {
      this.diemSpawn -= 1;
      this.sinhKhach(state, duLieu);
    }

    const quay = this.quayThuNgan(state);
    const slots = quay ? this.oHangDoi(quay, state, duLieu) : [];

    // Hàng đợi tiến lên khi có chỗ trống phía trước (chỉ khi đang đứng yên).
    const hangCho = this.dsKhach
      .filter((k) => k.giaiDoan === 'cho-thanh-toan')
      .sort((a, b) => a.id - b.id);
    hangCho.forEach((k, idx) => {
      const slot = slots[idx];
      if (!slot || k.chiSo < k.duong.length - 1) return;
      const oHienTai = k.duong[k.chiSo]!;
      if (oHienTai.x === slot.x && oHienTai.y === slot.y) return;
      const duongMoi = timDuong(oHienTai, slot, state.boCuc, kv, duLieu);
      if (duongMoi) {
        k.duong = duongMoi;
        k.chiSo = 0;
      }
    });

    for (const k of [...this.dsKhach]) {
      if (k.giaiDoan === 'den-ke') {
        if (this.diChuyen(k, dtGiay, kv.rong, kv.cao)) {
          // Tới kệ → đi ra quầy (hoặc xếp hàng).
          const oTTQuay = quay ? oTuongTac(quay, state.boCuc, kv, duLieu) : null;
          const hienTai = k.duong[k.duong.length - 1]!;
          if (quay && oTTQuay) {
            const duongRaQuay = timDuong(hienTai, oTTQuay, state.boCuc, kv, duLieu);
            if (!duongRaQuay) {
              this.loaiBo(k);
              continue;
            }
            const dangCho = this.dsKhach.filter(
              (x) => x.giaiDoan === 'cho-thanh-toan' || x.giaiDoan === 'thanh-toan',
            ).length;
            if (!this.dangThanhToan && dangCho === 0) {
              k.giaiDoan = 'thanh-toan';
              k.duong = duongRaQuay;
              k.chiSo = 0;
              k.henGio = THOI_GIAN_THANH_TOAN;
              this.dangThanhToan = true;
            } else if (slots.length > dangCho) {
              const slot = slots[dangCho]!;
              const duongRaSlot = timDuong(hienTai, slot, state.boCuc, kv, duLieu);
              if (duongRaSlot) {
                k.giaiDoan = 'cho-thanh-toan';
                k.duong = duongRaSlot;
                k.chiSo = 0;
              } else {
                this.loaiBo(k);
              }
            } else {
              // Hết chỗ xếp hàng: đi thẳng về cửa.
              k.giaiDoan = 'ra-cua';
              const cua = oCuaRaVao(kv);
              k.duong = timDuong(hienTai, cua, state.boCuc, kv, duLieu) ?? [hienTai, cua];
              k.chiSo = 0;
            }
          } else {
            this.loaiBo(k);
          }
        }
      } else if (k.giaiDoan === 'cho-thanh-toan') {
        if (this.diChuyen(k, dtGiay, kv.rong, kv.cao)) {
          // Đứng chờ; kiểm tra có tới lượt không.
          const hangCho = this.dsKhach
            .filter((x) => x.giaiDoan === 'cho-thanh-toan')
            .sort((a, b) => a.id - b.id);
          const viTri = hangCho.findIndex((x) => x.id === k.id);
          if (viTri === 0 && !this.dangThanhToan && quay) {
            const oTTQuay = oTuongTac(quay, state.boCuc, kv, duLieu);
            const hienTai = k.duong[k.duong.length - 1]!;
            if (oTTQuay) {
              const duongRaQuay = timDuong(hienTai, oTTQuay, state.boCuc, kv, duLieu);
              if (duongRaQuay) {
                k.giaiDoan = 'thanh-toan';
                k.duong = duongRaQuay;
                k.chiSo = 0;
                k.henGio = THOI_GIAN_THANH_TOAN;
                this.dangThanhToan = true;
              }
            }
          }
        }
      } else if (k.giaiDoan === 'thanh-toan') {
        if (k.chiSo < k.duong.length - 1) {
          this.diChuyen(k, dtGiay, kv.rong, kv.cao);
        } else {
          k.henGio -= dtGiay;
          if (k.henGio <= 0) {
            this.dangThanhToan = false;
            k.giaiDoan = 'ra-cua';
            const cua = oCuaRaVao(kv);
            const hienTai = k.duong[k.duong.length - 1]!;
            k.duong = timDuong(hienTai, cua, state.boCuc, kv, duLieu) ?? [hienTai, cua];
            k.chiSo = 0;
          }
        }
      } else {
        // ra-cua
        if (this.diChuyen(k, dtGiay, kv.rong, kv.cao)) {
          this.loaiBo(k);
        }
      }
    }
  }
}
